import React, { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiKey, FiCopy, FiEye, FiEyeOff } from 'react-icons/fi';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import projectService from '../services/projectService';
import { toast } from 'react-toastify';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await projectService.getProjects();
      setProjects(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreate = () => {
    setSelectedProject(null);
    setFormData({
      name: '',
      display_name: '',
      description: ''
    });
    setShowModal(true);
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name,
      display_name: project.display_name,
      description: project.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (selectedProject) {
        await projectService.updateProject(selectedProject.id, formData);
        toast.success('Project updated successfully');
      } else {
        const response = await projectService.createProject(formData);
        setCredentials({
          api_key: response.data.api_key,
          api_secret: response.data.api_secret,
          webhook_secret: response.data.webhook_secret
        });
        setShowCredentialsModal(true);
        toast.success('Project created successfully');
      }
      setShowModal(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await projectService.deleteProject(selectedProject.id);
      toast.success('Project deleted successfully');
      setShowDeleteModal(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete project');
    }
  };

  const handleRegenerateClick = (project) => {
    setSelectedProject(project);
    setShowRegenerateModal(true);
  };

  const handleRegenerateCredentials = async () => {
    setRegenerating(true);
    try {
      const response = await projectService.regenerateCredentials(selectedProject.id);
      setCredentials({
        api_key: response.data.api_key,
        api_secret: response.data.api_secret,
        webhook_secret: response.data.webhook_secret
      });
      setShowRegenerateModal(false);
      setShowCredentialsModal(true);
      toast.success('Credentials regenerated successfully');
    } catch (error) {
      toast.error('Failed to regenerate credentials');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const columns = [
    {
      key: 'display_name',
      label: 'Project',
      render: (value, row) => (
        <div className="project-cell">
          <span className="project-name">{value}</span>
          <span className="project-slug">{row.name}</span>
        </div>
      )
    },
    {
      key: 'api_key',
      label: 'API Key',
      render: (value) => (
        <div className="api-key-cell">
          <code>{value?.substring(0, 20)}...</code>
          <button className="copy-btn" onClick={() => copyToClipboard(value, 'API Key')}>
            <FiCopy />
          </button>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'status-active' : 'status-inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button className="action-btn" onClick={() => handleEdit(row)} title="Edit">
            <FiEdit2 />
          </button>
          <button className="action-btn" onClick={() => handleRegenerateClick(row)} title="Regenerate Credentials">
            <FiKey />
          </button>
          <button className="action-btn danger" onClick={() => { setSelectedProject(row); setShowDeleteModal(true); }} title="Delete">
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout title="Projects">
      <div className="projects-page">
        <Card
          title="Projects"
          subtitle="Manage external projects that can submit payout transactions"
          actions={
            <Button onClick={handleCreate}>
              <FiPlus /> Add Project
            </Button>
          }
        >
          <Table
            columns={columns}
            data={projects}
            loading={loading}
            emptyMessage="No projects found. Create your first project to get started."
          />
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedProject ? 'Edit Project' : 'Create Project'}
      >
        <form onSubmit={handleSubmit} className="project-form">
          <Input
            label="Project Name (slug)"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
            placeholder="my-project"
            disabled={!!selectedProject}
            required
          />
          <Input
            label="Display Name"
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            placeholder="My Project"
            required
          />
          <Input
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional description"
          />

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {selectedProject ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Credentials Modal */}
      <Modal
        isOpen={showCredentialsModal}
        onClose={() => { setShowCredentialsModal(false); setShowSecret(false); }}
        title="API Credentials"
      >
        <div className="credentials-modal">
          <div className="credentials-warning">
            Please save these credentials now. The API Secret will not be shown again.
          </div>

          <div className="credential-item">
            <label>API Key</label>
            <div className="credential-value">
              <code>{credentials?.api_key}</code>
              <button onClick={() => copyToClipboard(credentials?.api_key, 'API Key')}>
                <FiCopy />
              </button>
            </div>
          </div>

          <div className="credential-item">
            <label>API Secret</label>
            <div className="credential-value">
              <code>{showSecret ? credentials?.api_secret : '••••••••••••••••••••'}</code>
              <button onClick={() => setShowSecret(!showSecret)}>
                {showSecret ? <FiEyeOff /> : <FiEye />}
              </button>
              <button onClick={() => copyToClipboard(credentials?.api_secret, 'API Secret')}>
                <FiCopy />
              </button>
            </div>
          </div>

          <div className="credential-item">
            <label>Webhook Secret</label>
            <div className="credential-value">
              <code>{credentials?.webhook_secret}</code>
              <button onClick={() => copyToClipboard(credentials?.webhook_secret, 'Webhook Secret')}>
                <FiCopy />
              </button>
            </div>
          </div>

          <div className="modal-actions">
            <Button onClick={() => { setShowCredentialsModal(false); setShowSecret(false); }}>
              I've Saved These
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Project"
      >
        <div className="delete-modal">
          <p>Are you sure you want to delete <strong>{selectedProject?.display_name}</strong>?</p>
          <p className="delete-warning">This action cannot be undone. Projects with existing transactions cannot be deleted.</p>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Regenerate Credentials Confirmation Modal */}
      <Modal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        title="Regenerate Credentials"
      >
        <div className="delete-modal">
          <p>Are you sure you want to regenerate credentials for <strong>{selectedProject?.display_name}</strong>?</p>
          <p className="delete-warning">This will invalidate all existing API credentials. Any integrations using the current credentials will stop working.</p>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowRegenerateModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRegenerateCredentials} loading={regenerating}>
              Regenerate
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default Projects;
